import { ChevronDownIcon, ChevronRightIcon, EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { type FC, useLayoutEffect, useRef, useState } from "react";

import { Tooltip } from "@fronterahealth/frontera-ui-components";

import { type ReportType } from "@pages/QASettings/qaRuleBlob";
import { type EditableRuleFields, REPORT_TYPES } from "@pages/QASettings/qaRuleFields";

// ---------------------------------------------------------------------------
// Structured rule editor pieces (ported from the portal's QACriteriaPackageCard).
// Shared between BaseQAPackageCard's inline per-rule editor (existing rules,
// PATCHed on blur) and BaseQAPackageForm's draft-rule editor (new rules held
// in local form state and serialized on package create) — see
// BaseQAPackageCard.tsx / BaseQAPackageForm.tsx for the two call sites.
// ---------------------------------------------------------------------------

// Grid shared by the rules header row and each collapsed rule summary row so
// the columns line up: name | report type | location | kebab. (No "enabled"
// column — the REST QA rule model has no per-rule enabled flag, only the
// package-level `is_globally_disabled` toggle.)
export const RULE_GRID = "grid grid-cols-[minmax(0,1fr)_84px_minmax(0,200px)_36px] items-center gap-3";
export const FIELD_LABEL = "text-[10px] font-semibold uppercase tracking-wider text-text-tertiary";
export const EDITABLE_FIELD =
  "border border-border-medium bg-bg-default focus:border-globemallow-600 focus:outline-none focus:ring-2 focus:ring-globemallow-600/20";

// ---------------------------------------------------------------------------
// Kebab menu (hand-rolled, ported from the portal's local KebabMenu — Mosaic
// doesn't export an icon-only popover-menu primitive from its package root).
// ---------------------------------------------------------------------------

export interface MenuItem {
  label: string;
  onClick: () => void;
  danger?: boolean;
}

export const KebabMenu: FC<{ ariaLabel: string; items: MenuItem[] }> = ({ ariaLabel, items }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative justify-self-end">
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
        className="grid h-7 w-7 place-items-center rounded text-text-tertiary hover:bg-bg-disabled hover:text-text-primary"
      >
        <EllipsisVerticalIcon className="h-5 w-5" />
      </button>
      {open && (
        <>
          {/* Full-screen backdrop closes the menu on any outside click. */}
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute right-0 top-8 z-30 min-w-[190px] rounded-lg border border-border-subtle bg-bg-surface p-1 shadow-lg">
            {items.map((it) => (
              <button
                key={it.label}
                type="button"
                onClick={() => {
                  setOpen(false);
                  it.onClick();
                }}
                className={clsx(
                  "block w-full rounded px-2.5 py-1.5 text-left text-sm hover:bg-bg-disabled",
                  it.danger ? "text-text-error-primary" : "text-text-primary",
                )}
              >
                {it.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Chip color depends on whether the type is selected and whether the chip is
// clickable — the collapsed row shows a read-only preview of the same chips.
const chipColor = (on: boolean, editable: boolean): string => {
  if (on) return "border-globemallow-600 bg-globemallow-600 text-white";
  if (editable) return "border-border-medium bg-bg-default text-text-secondary hover:border-globemallow-600";
  return "border-dashed border-border-subtle bg-transparent text-text-tertiary";
};

export const ReportTypeChips: FC<{
  types: ReportType[];
  editable: boolean;
  onToggle: (t: ReportType) => void;
}> = ({ types, editable, onToggle }) => (
  <div className="flex gap-1.5">
    {REPORT_TYPES.map((t) => {
      const on = types.includes(t);
      return (
        <button
          key={t}
          type="button"
          disabled={!editable}
          onClick={() => onToggle(t)}
          aria-pressed={on}
          className={clsx(
            "rounded border px-2.5 py-1 text-[11px] font-semibold tracking-wide transition-colors",
            editable ? "cursor-pointer" : "cursor-default",
            chipColor(on, editable),
          )}
        >
          {t}
        </button>
      );
    })}
  </div>
);

export const FieldLabelRow: FC<{ label: string; info?: string }> = ({ label, info }) => (
  <div className="mb-1.5 flex items-center gap-1">
    <span className={FIELD_LABEL}>{label}</span>
    {info && <Tooltip text={info} placement="top" showInfoIcon infoIconClassName="h-3.5 w-3.5 text-text-tertiary" />}
  </div>
);

export const InlineInput: FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur: () => void;
}> = ({ label, value, onChange, onBlur }) => (
  <div>
    <FieldLabelRow label={label} />
    <input
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      className={clsx("w-full rounded-md px-2.5 py-1.5 text-sm text-text-primary", EDITABLE_FIELD)}
    />
  </div>
);

export const AutoTextArea: FC<{
  label: string;
  value: string;
  info?: string;
  onChange: (v: string) => void;
  onBlur: () => void;
}> = ({ label, value, info, onChange, onBlur }) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  // Grow to fit content (capped) so long term lists are readable without an
  // inner scrollbar — mirrors the portal's autosize behaviour.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    const need = el.scrollHeight + 2;
    const cap = 340;
    el.style.height = `${Math.min(need, cap)}px`;
    el.style.overflowY = need > cap ? "auto" : "hidden";
  }, [value]);
  return (
    <div className="mt-3.5">
      <FieldLabelRow label={label} info={info} />
      <textarea
        ref={ref}
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className={clsx(
          "block w-full resize-none rounded-md px-2.5 py-2 text-sm leading-relaxed text-text-primary",
          EDITABLE_FIELD,
        )}
      />
    </div>
  );
};

export interface RuleItemHandlers {
  onToggleExpand: () => void;
  onChangeField: (patch: Partial<EditableRuleFields>) => void;
  onToggleType: (t: ReportType) => void;
  onDelete: () => void;
  onBlurSave: () => void;
}

export const RuleItem: FC<{
  fields: EditableRuleFields;
  expanded: boolean;
  handlers: RuleItemHandlers;
}> = ({ fields, expanded, handlers }) => {
  const menuItems: MenuItem[] = [{ label: "Delete", onClick: handlers.onDelete, danger: true }];
  const chevron = (
    <span className="flex-shrink-0 text-text-tertiary">
      {expanded ? <ChevronDownIcon className="h-3.5 w-3.5" /> : <ChevronRightIcon className="h-3.5 w-3.5" />}
    </span>
  );
  const kebab = <KebabMenu ariaLabel={`Actions for ${fields.rule || "rule"}`} items={menuItems} />;

  return (
    <div className={clsx("border-t border-border-subtle", expanded && "bg-bg-default/60")}>
      {expanded ? (
        // Slim header while open — name / report type / location all live in
        // the editor below, so repeating them in a summary row here would
        // just duplicate everything.
        <div className="flex items-center gap-3 px-4 py-2.5 pl-6">
          <button type="button" onClick={handlers.onToggleExpand} aria-label="Collapse rule">
            {chevron}
          </button>
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-text-primary">
            {fields.rule || "Untitled rule"}
          </span>
          {kebab}
        </div>
      ) : (
        <div className={clsx(RULE_GRID, "px-4 py-3 pl-6")}>
          <button type="button" onClick={handlers.onToggleExpand} className="flex min-w-0 items-center gap-2 text-left">
            {chevron}
            <span className="truncate text-sm font-medium text-text-primary">{fields.rule || "Untitled rule"}</span>
          </button>
          <ReportTypeChips types={fields.types} editable={false} onToggle={() => {}} />
          <div className="truncate text-sm text-text-tertiary" title={fields.location}>
            {fields.location}
          </div>
          {kebab}
        </div>
      )}

      {expanded && (
        <div className="px-4 pb-4 pl-12 pr-8">
          <div className="grid grid-cols-1 gap-3.5 md:grid-cols-[minmax(0,1.5fr)_140px_minmax(0,1fr)]">
            <InlineInput
              label="Rule name"
              value={fields.rule}
              onChange={(v) => handlers.onChangeField({ rule: v })}
              onBlur={handlers.onBlurSave}
            />
            <div>
              <FieldLabelRow label="Report type" />
              <ReportTypeChips types={fields.types} editable onToggle={handlers.onToggleType} />
            </div>
            <InlineInput
              label="Location in report"
              value={fields.location}
              onChange={(v) => handlers.onChangeField({ location: v })}
              onBlur={handlers.onBlurSave}
            />
          </div>

          <AutoTextArea
            label="What to scan for"
            value={fields.scanFor}
            onChange={(v) => handlers.onChangeField({ scanFor: v })}
            onBlur={handlers.onBlurSave}
          />
          <AutoTextArea
            label="Feedback message"
            value={fields.feedback}
            info="Text in [brackets] is filled in by the scanner with what it found."
            onChange={(v) => handlers.onChangeField({ feedback: v })}
            onBlur={handlers.onBlurSave}
          />
        </div>
      )}
    </div>
  );
};
